import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, Copy, Check, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface RefreshTokenAssistantProps {
  clientId: string;
  clientSecret: string;
  onTokenGenerated: (refreshToken: string) => void;
}

export const RefreshTokenAssistant = ({ 
  clientId, 
  clientSecret,
  onTokenGenerated 
}: RefreshTokenAssistantProps) => {
  const [authCode, setAuthCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // Construct the authorization URL
  const redirectUri = "urn:ietf:wg:oauth:2.0:oob";
  const authUrl = `https://sellercentral.amazon.fr/apps/authorize/consent?application_id=${clientId}&state=stateexample&version=beta`;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(authUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "URL copiée",
        description: "Collez-la dans votre navigateur pour autoriser l'application",
      });
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleExchangeCode = async () => {
    if (!authCode.trim()) {
      toast({
        title: "Code manquant",
        description: "Veuillez entrer le code d'autorisation",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('spapi-oauth-exchange', {
        body: {
          code: authCode.trim(),
          client_id: clientId,
          client_secret: clientSecret,
        }
      });

      if (error) throw error;

      if (data?.success && data?.refresh_token) {
        toast({
          title: "Refresh Token généré",
          description: "Le token a été récupéré avec succès",
        });
        onTokenGenerated(data.refresh_token);
        setAuthCode("");
      } else {
        throw new Error(data?.message || "Échec de l'échange du code");
      }
    } catch (error: any) {
      console.error("Token exchange error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'échanger le code d'autorisation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/20 bg-card/50">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-primary" />
          Assistant de génération du Refresh Token
        </CardTitle>
        <CardDescription>
          Suivez ces étapes pour obtenir un nouveau refresh token
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Étape 1 : Autoriser l'application</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Cliquez sur le bouton ci-dessous ou copiez l'URL pour autoriser votre application SP-API dans Seller Central
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open(authUrl, '_blank')}
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ouvrir Seller Central
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyUrl}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="auth-code" className="text-sm font-medium">
            Étape 2 : Collez le code d'autorisation
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Après avoir autorisé l'application, Amazon vous fournira un code. Collez-le ici :
          </p>
          <Input
            id="auth-code"
            type="text"
            placeholder="Coller le code d'autorisation ici..."
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value)}
            className="font-mono text-sm"
          />
        </div>

        <Button 
          type="button"
          onClick={handleExchangeCode}
          disabled={loading || !authCode.trim()}
          className="w-full"
        >
          {loading ? "Échange en cours..." : "Générer le Refresh Token"}
        </Button>

        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
          <strong>Note :</strong> Le refresh token sera automatiquement enregistré dans vos credentials une fois généré.
        </div>
      </CardContent>
    </Card>
  );
};
